import { IntlShape } from 'react-intl';
import { isMiracleEar } from '../../../hooks/miracle-ear-hook';
import {
    IAudiogram,
    IOption,
    IOrderDetails,
    IProductDetail,
    IReceiverPart,
    ISelectedOptionIds,
    sides,
} from '../../../models/product-detail';
import { FieldValidation, validate } from '../../../validation/validation';
import * as Validators from '../../../validation/validators';
import {
    BATTERY_PRODUCT_TYPE,
    CartItemTypes,
    noReceiverOption,
    RECEIVERS_MAX_REST,
    RECEIVERS_MAX_USCAFR,
    RECEIVER_LESS_OR_EQUAL_TO,
    RECEIVER_OPTIONS,
    thresholds as frequency,
    noChargerValidationCountries,
    CHARGER_OPTIONS_ID_LIST,
    IMPRESSIONS_OPTIONS,
} from '../../common/constants';
export class ProductOrderFormErrors {
    quantity? = '';
    firstName? = '';
    lastName? = '';
    ageRange? = '';
    patientNumber? = '';
    receivers? = '';
    impressions? = '';
    scanOnFileRight? = '';
    scanOnFileLeft? = '';
    otoscanPackageId1? = '';
    otoscanPackageId2? = '';
    impressionRight? = '';
    impressionLeft? = '';
    right500? = '';
    right1k? = '';
    right2k? = '';
    right4k? = '';
    left500? = '';
    left1k? = '';
    left2k? = '';
    left4k? = '';
    rightSelectedIds? = '';
    leftSelectedIds? = '';
    rightEarmoldIds? = '';
    leftEarmoldIds? = '';
    earmoldDesignId? = '';
    pairAid? = '';
    crosPair? = '';
    chargers? = '';
}

//#region field Validations

const firstNameValidation = new FieldValidation('firstName', 'First Name', [
    Validators.required(),
]);
const lastNameValidation = new FieldValidation('lastName', 'Last Name', [
    Validators.required(),
]);
const patientRefValidation = new FieldValidation(
    'lastName',
    'Patient Reference #',
    [Validators.required(), Validators.patientRefRegex()]
);
const ageRangeValidation = new FieldValidation('ageRange', 'Age Range', [
    Validators.required(),
]);
const patientNumberValidation = new FieldValidation(
    'agreementReference',
    'Patient #',
    [Validators.required()]
);
const impressionsValidation = new FieldValidation(
    'impressions',
    'Impressions',
    [Validators.required()]
);
const scanOnFileRightValidation = new FieldValidation(
    'scanOnFileRight',
    'Digital Impressions On File',
    [Validators.required()]
);

const scanOnFileLeftValidation = new FieldValidation(
    'scanOnFileLeft',
    'Digital Impressions On File',
    [Validators.required()]
);
const impressionRightValidation = new FieldValidation(
    'impressionRight',
    'Attach Digital Impressions',
    [Validators.required()]
);
const impressionLeftValidation = new FieldValidation(
    'impressionLeft',
    'Attach Digital Impressions',
    [Validators.required()]
);
const otoscanPackageId1Validation = new FieldValidation(
    'otoscanPackageId1',
    'Direct Ear Scan',
    [Validators.required()]
);
const otoscanPackageId2Validation = new FieldValidation(
    'otoscanPackageId2',
    'Direct Ear Scan',
    [Validators.required()]
);

const right500Validation = new FieldValidation('right500', 'Right Audiogram', [
    Validators.required(),
]);

const right1kValidation = new FieldValidation('right1k', 'Right Audiogram', [
    Validators.required(),
]);

const right2kValidation = new FieldValidation('right2k', 'Right Audiogram', [
    Validators.required(),
]);

const right4kValidation = new FieldValidation('right4k', 'Right Audiogram', [
    Validators.required(),
]);

const left500Validation = new FieldValidation('left500', 'Left Audiogram', [
    Validators.required(),
]);

const left1kValidation = new FieldValidation('left1k', 'Left Audiogram', [
    Validators.required(),
]);

const left2kValidation = new FieldValidation('left2k', 'Left Audiogram', [
    Validators.required(),
]);

const left4kValidation = new FieldValidation('left4k', 'Left Audiogram', [
    Validators.required(),
]);

const earmoldDesignIdValidation = new FieldValidation(
    'earmoldDesignId',
    'Earmold style',
    [Validators.required()]
);

const crosPairValidation = new FieldValidation('crosPair', 'Cros Pair device', [
    Validators.required(),
]);
//#endregion field Validations

export const flattenObject = (obj: any) => {
    const flattened: any = {};

    Object.keys(obj).forEach((key) => {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
            Object.assign(flattened, flattenObject(obj[key]));
        } else {
            flattened[key] = obj[key];
        }
    });

    return flattened;
};
export const findReceiverDuplicates = (
    receivers: IReceiverPart[],
    keySelector: (receiver: IReceiverPart) => void,
    valueSelector: (receiver: IReceiverPart) => void
) => {
    const duplicatesMap = new Map();

    for (const obj of receivers) {
        const key = keySelector(obj);
        const value = valueSelector(obj);

        if (!duplicatesMap.has(key)) {
            duplicatesMap.set(key, value);
        } else {
            const currentValue = duplicatesMap.get(key);
            duplicatesMap.set(key, currentValue + value);
        }
    }

    for (const item of duplicatesMap.values()) {
        if (item > 150) {
            return true;
        }
    }
};

const IsValid = (
    orderState: IOrderDetails,
    productDetailState: IProductDetail,
    patientNumberRequired: boolean,
    countryCode: string,
    isEdit: boolean,
    siteNumber: number,
    intl: IntlShape
) => {
    const flattenedOrderState: any = flattenObject(orderState);
    const fieldValidations: FieldValidation[] = [];
    const isEarmold = [
        CartItemTypes.Earmold,
        CartItemTypes.BteWithEarmold,
        CartItemTypes.RicWithEarmold,
        CartItemTypes.SoundGearEarmold,
    ].includes(orderState!.cartItemTypeId!);

    if (orderState.receivers) {
        const receiversWithoutNoReceiver = orderState.receivers.filter(
            (receiver) => receiver.cableLength !== noReceiverOption
        );
        orderState.receivers = receiversWithoutNoReceiver;
    }

    if (orderState.orderType === 'patient') {
        if (isMiracleEar(siteNumber)) {
            if (!isEdit) {
                fieldValidations.push(patientRefValidation);
                fieldValidations.push(ageRangeValidation);
            }
        } else {
            fieldValidations.push(firstNameValidation);
            fieldValidations.push(lastNameValidation);
            fieldValidations.push(ageRangeValidation);
        }
        if (patientNumberRequired) {
            fieldValidations.push(patientNumberValidation);
        }
    }

    if (
        [
            CartItemTypes.CustomOnly,
            CartItemTypes.BteWithEarmold,
            CartItemTypes.RicWithEarmold,
            CartItemTypes.RicWithAp,
            CartItemTypes.ApReceiver,
            CartItemTypes.Earmold,
            CartItemTypes.SoundGearEarmold,
            CartItemTypes.SoundGearCustom,
        ].includes(orderState!.cartItemTypeId!)
    ) {
        fieldValidations.push(impressionsValidation);
        if (orderState.impressions === IMPRESSIONS_OPTIONS[1].defaultMessage) {
            if (orderState.side === 'R' || orderState.side === 'B') {
                fieldValidations.push(scanOnFileRightValidation);
            }
            if (orderState.side === 'L' || orderState.side === 'B') {
                fieldValidations.push(scanOnFileLeftValidation);
            }
        }
        if (orderState.impressions === IMPRESSIONS_OPTIONS[3].defaultMessage) {
            if (orderState.side === 'R' || orderState.side === 'B') {
                fieldValidations.push(impressionRightValidation);
            }
            if (orderState.side === 'L' || orderState.side === 'B') {
                fieldValidations.push(impressionLeftValidation);
            }
        }
        if (orderState.impressions === IMPRESSIONS_OPTIONS[2].defaultMessage) {
            if (orderState.side === 'R' || orderState.side === 'B') {
                fieldValidations.push(otoscanPackageId1Validation);
            }
            if (orderState.side === 'L' || orderState.side === 'B') {
                fieldValidations.push(otoscanPackageId2Validation);
            }
        }
    }

    if (
        [
            CartItemTypes.ApReceiver,
            CartItemTypes.BteWithEarmold,
            CartItemTypes.CustomOnly,
            CartItemTypes.RicWithEarmold,
            CartItemTypes.RicWithAp,
        ].includes(orderState!.cartItemTypeId!) ||
        ['RIC Earmold', 'Standard Earmold', 'Thin Tube Earmold'].includes(
            orderState.earmoldCategory!
        )
    ) {
        const audiogram: IAudiogram = JSON.parse(
            orderState!.patient!.audiogram
        );

        [sides.right, sides.left].forEach((x: sides) => {
            const curve =
                x === sides.right
                    ? audiogram.right.air.curve.split(',')
                    : audiogram.left.air.curve.split(',');
            ['500', '1k', '2k', '4k'].forEach((y) => {
                flattenedOrderState[`${x}${y}`] = curve[frequency.indexOf(y)];
            });
        });

        if (orderState.side === 'R' || orderState.side === 'B') {
            fieldValidations.push(right500Validation);
            fieldValidations.push(right1kValidation);
            fieldValidations.push(right2kValidation);
            fieldValidations.push(right4kValidation);
        }
        if (orderState.side === 'L' || orderState.side === 'B') {
            fieldValidations.push(left500Validation);
            fieldValidations.push(left1kValidation);
            fieldValidations.push(left2kValidation);
            fieldValidations.push(left4kValidation);
        }
    }

    if (
        (isEarmold && !orderState!.cros) ||
        (isEarmold && orderState!.cros && orderState!.side === 'B')
    ) {
        fieldValidations.push(earmoldDesignIdValidation);
    }

    ///earmold design validation for monaural cros
    if (
        isEarmold &&
        orderState!.cros &&
        orderState!.side !== 'B' &&
        (((orderState!.rightSelectedIds.includes(510) ||
            orderState!.leftSelectedIds.includes(510)) &&
            orderState!.showEarmoldForMonauralCros) ||
            orderState!.rightSelectedIds.includes(511) ||
            orderState!.leftSelectedIds.includes(511))
    ) {
        fieldValidations.push(earmoldDesignIdValidation);
    }

    ///Cros Pair validation for monaural cros
    if (
        orderState &&
        orderState.cros &&
        orderState.side !== 'B' &&
        orderState.orderType === 'patient'
    ) {
        fieldValidations.push(crosPairValidation);
    }

    const errors: ProductOrderFormErrors = validate(
        flattenedOrderState,
        fieldValidations
    );

    ///pairAid validation
    /// had to do a manual validation because the flattened object doesnt retain pairAid object
    if (
        orderState!.cros &&
        (orderState!.rightSelectedIds.includes(511) ||
            orderState!.leftSelectedIds.includes(511))
    ) {
        if (!orderState.pairAid) {
            errors.pairAid = 'Hearing Aid technology';
        } else {
            delete errors.pairAid;
        }
    }

    //quantity validation temporary validation untill validation thunk is fixed
    if (orderState.orderType === 'bulk') {
        if (orderState.quantity <= 0) {
            errors.quantity = 'Quantity is Required';
        } else if (orderState.quantity > 500) {
            errors.quantity = 'Quantity cannot exceed 500';
        } else {
            delete errors.quantity;
        }
    }

    ///Ric recievers validation
    if (
        (orderState.orderType === 'bulk' &&
            orderState.cartItemTypeId === CartItemTypes.RicWithStockReceiver) ||
        orderState.additionalReceivers
    ) {
        if (orderState.receivers && orderState.receivers.length > 0) {
            const isValid = orderState.receivers.every((receiver) => {
                if (
                    orderState.cros &&
                    orderState.leftSelectedIds.includes(510)
                ) {
                    if (
                        receiver.side &&
                        receiver.cableLength &&
                        receiver.quantity &&
                        receiver.quantity > 0
                    ) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    if (
                        receiver.side &&
                        receiver.cableLength &&
                        receiver.gain &&
                        receiver.quantity &&
                        receiver.quantity > 0
                    ) {
                        return true;
                    } else {
                        return false;
                    }
                }
            });

            if (!isValid) {
                errors.receivers = RECEIVER_OPTIONS;
            } else {
                const totalReceiversQuantity = orderState!.receivers!.reduce(
                    (x, y) => x + parseInt(y.quantity!.toString()),
                    0
                );

                if (!['US', 'CA', 'FR', 'DE'].includes(countryCode)) {
                    if (totalReceiversQuantity > 500) {
                        errors.receivers = RECEIVERS_MAX_REST;
                    } else if (totalReceiversQuantity > orderState!.quantity) {
                        errors.receivers = RECEIVER_LESS_OR_EQUAL_TO;
                    } else {
                        delete errors.receivers;
                    }
                } else {
                    if (
                        findReceiverDuplicates(
                            orderState!.receivers,
                            (r) => {
                                const { quantity, ...rest } = r;
                                return Object.values(rest).join('');
                            },
                            (r) => r.quantity
                        )
                    ) {
                        errors.receivers = RECEIVERS_MAX_USCAFR;
                    } else {
                        delete errors.receivers;
                    }
                }
            }
        }
    }

    // Earmold options validation
    if (isEarmold) {
        if (orderState.side === 'R' || orderState.side === 'B') {
            if (productDetailState.earmoldOptions) {
                errors.rightEarmoldIds = optionsValidation(
                    {
                        rightSelectedIds: orderState.rightEarmoldIds,
                        leftSelectedIds: orderState.leftEarmoldIds,
                    },
                    productDetailState.earmoldOptions,
                    sides.right
                );
                errors.rightEarmoldIds = errors.rightEarmoldIds
                    ? 'Earmold ' + errors.rightEarmoldIds
                    : undefined;
                if (!errors.rightEarmoldIds) {
                    delete errors.rightEarmoldIds;
                }
            }
        }
        if (orderState.side === 'L' || orderState.side === 'B') {
            if (productDetailState.earmoldOptions) {
                errors.leftEarmoldIds = optionsValidation(
                    {
                        rightSelectedIds: orderState.rightEarmoldIds,
                        leftSelectedIds: orderState.leftEarmoldIds,
                    },
                    productDetailState.earmoldOptions,
                    sides.left
                );
                errors.leftEarmoldIds = errors.leftEarmoldIds
                    ? 'Earmold ' + errors.leftEarmoldIds
                    : undefined;
                if (!errors.leftEarmoldIds) {
                    delete errors.leftEarmoldIds;
                }
            }
        }
    }

    // product options validation
    if (![CartItemTypes.Earmold].includes(orderState!.cartItemTypeId!))
        if (orderState.side === 'R' || orderState.side === 'B') {
            if (productDetailState.options) {
                errors.rightSelectedIds = optionsValidation(
                    {
                        rightSelectedIds: orderState.rightSelectedIds,
                        leftSelectedIds: orderState.leftSelectedIds,
                    },
                    productDetailState.options,
                    sides.right
                );
                errors.rightSelectedIds = errors.rightSelectedIds
                    ? 'Product ' + errors.rightSelectedIds
                    : undefined;
                if (!errors.rightSelectedIds) {
                    delete errors.rightSelectedIds;
                }
            }
        }
    if (orderState.side === 'L' || orderState.side === 'B') {
        if (productDetailState.options) {
            errors.leftSelectedIds = optionsValidation(
                {
                    rightSelectedIds: orderState.rightSelectedIds,
                    leftSelectedIds: orderState.leftSelectedIds,
                },
                productDetailState.options,
                sides.left
            );
            errors.leftSelectedIds = errors.leftSelectedIds
                ? 'Product ' + errors.leftSelectedIds
                : undefined;
            if (!errors.leftSelectedIds) {
                delete errors.leftSelectedIds;
            }
        }
    }

    // Charger options validation
    if (
        !noChargerValidationCountries.includes(countryCode) &&
        orderState.orderType === 'bulk' &&
        productDetailState.options.some((option) =>
            CHARGER_OPTIONS_ID_LIST.includes(option.id)
        )
    ) {
        const filterChargerAccessories = orderState!.accessories!.filter(
            (accessory) =>
                accessory.isCharger ||
                accessory.productType === BATTERY_PRODUCT_TYPE
        );
        const totalChargersQuantity = filterChargerAccessories!.reduce(
            (acc, charger) => acc + parseInt(charger.quantity!.toString()),
            0
        );
        const getStockOrderChargersQuantity = (): number =>
            Math.round(orderState!.quantity / 2);

        if (
            totalChargersQuantity &&
            totalChargersQuantity > getStockOrderChargersQuantity()
        ) {
            errors.chargers = intl.formatMessage({
                id: 'Max_Chargers',
                defaultMessage: 'Maximum number of chargers per order exceeded',
            });
        } else if (
            !totalChargersQuantity ||
            totalChargersQuantity < getStockOrderChargersQuantity()
        ) {
            errors.chargers = intl.formatMessage({
                id: 'Min_Chargers',
                defaultMessage:
                    'Must select maximum number of chargers to continue',
            });
        } else {
            delete errors.chargers;
        }
    }

    // Bug 97759
    // Since there are no other options to edit on these orders just get rid of the needless errors
    if (
        isEdit &&
        isMiracleEar(siteNumber) &&
        orderState.orderType === 'patient' &&
        orderState.receivers
    ) {
        delete errors.leftSelectedIds;
        delete errors.rightSelectedIds;
    }
    return errors;
};

export const isOptionCategoryRequired = (
    optionCategory: IOption,
    selectedIds: ISelectedOptionIds,
    side: sides
) => {
    let valid = true;

    optionCategory.items.forEach((x) => {
        if (
            [
                'single',
                'dual-single',
                'dual-single-nd',
                'single, inline',
                'select-nd',
                'dual-select-nd',
            ].includes(x.kind)
        ) {
            if (side === sides.binaural || side === sides.right) {
                valid =
                    !x.items.some((x) => {
                        return x.isAvailableRight;
                    }) ||
                    x.items.some((item) => {
                        return selectedIds.rightSelectedIds.includes(item.id);
                    });
            }
            if (side === sides.binaural || side === sides.left) {
                valid =
                    valid &&
                    (!x.items.some((x) => {
                        return x.isAvailableLeft;
                    }) ||
                        x.items.some((item) => {
                            return selectedIds.leftSelectedIds.includes(
                                item.id
                            );
                        }));
            }
        }
    });

    return !valid;
};

const optionsValidation = (
    selectedIds: ISelectedOptionIds,
    options: IOption[],
    side: sides
) => {
    if (
        options.some((x) => {
            return isOptionCategoryRequired(x, selectedIds, side);
        })
    ) {
        return 'Options is required';
    } else {
        return undefined;
    }
};

export default IsValid;
